import { Wrapper, Heading, Text, LayoutType, Layout } from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type LayoutStructurePageProps = {};

const LayoutStructurePage = ({ }: LayoutStructurePageProps) => {
    return (
        <div className="container-fluid p-2">
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Layout
            </Heading>
            <Text className="mb-4">
                The Layout component provides a structured way to organize an application's interface using sections such as <Text el="strong">Header, Sidebar, Content, and Footer.</Text>
                It ensures a consistent look and feel across your application while allowing flexibility to include or omit sections depending on the use case.
            </Text>
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Layout (with Sidebar, Header and Footer)
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <Layout type={LayoutType.Sidebar}>
                        <Layout.Header>
                            <Wrapper className="p-3">
                                Header
                            </Wrapper>
                        </Layout.Header>
                        <Layout.Sidebar>
                            <Wrapper className="p-3">
                                Sidebar
                            </Wrapper>
                        </Layout.Sidebar>
                        <Layout.Content>
                            <Wrapper className="p-3">
                                Content
                            </Wrapper>
                        </Layout.Content>
                        <Layout.Footer>
                            <Wrapper className="p-3">
                                Footer
                            </Wrapper>
                        </Layout.Footer>
                    </Layout>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {layoutSidebarCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h5" className="fw-medium fs-5">
                Layout Header
            </Heading>
            <Text className="mb-4">
                Defines the top section of the layout.
                Commonly used for navigation bars, branding, or user menus.
            </Text>

            <Heading el="h5" className="fw-medium fs-5">
                Layout Sidebar
            </Heading>
            <Text className="mb-4">
                Defines the side navigation panel.
                Commonly used for navigation links, menus, or filters.
            </Text>

            <Heading el="h5" className="fw-medium fs-5">
                Layout Content
            </Heading>
            <Text className="mb-4">
                Defines the main content area.
                Holds the dynamic content of your application (dashboards, forms, etc.).
            </Text>

            <Heading el="h5" className="fw-medium fs-5">
                Layout Footer
            </Heading>
            <Text className="mb-4">
                Defines the bottom section of the layout.
                Commonly used for copyright, contact links, or secondary navigation.
            </Text>

            <Heading el="h5" className="fw-medium fs-5">
                Wrapper
            </Heading>
            <Text className="mb-4">
                A utility component that ensures consistent spacing and styling.
                Example: className="p-3" applies Bootstrap padding.
            </Text>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Full height Sidebar
            </Heading>

            <Text className="mb-4">
                Setting <Text el="code" className="fw-medium fs-6">{`type={LayoutType.FullSidebar}`}</Text> ensures that the sidebar extends to the full height of the layout, providing a consistent and structured appearance.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Layout type={LayoutType.FullSidebar}>
                        <Layout.Header>
                            <Wrapper className="p-3">
                                Header
                            </Wrapper>
                        </Layout.Header>
                        <Layout.Sidebar>
                            <Wrapper className="p-3">
                                Sidebar
                            </Wrapper>
                        </Layout.Sidebar>
                        <Layout.Content>
                            <Wrapper className="p-3">
                                Content
                            </Wrapper>
                        </Layout.Content>
                        <Layout.Footer>
                            <Wrapper className="p-3">
                                Footer
                            </Wrapper>
                        </Layout.Footer>
                    </Layout>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {layoutSidebarFullCode}
                </CodeHighlighter>
            </CodeSnippet>
        </div>
    );
}
export default LayoutStructurePage;

const layoutSidebarCode = `<Layout type={LayoutType.Sidebar}>
    <Layout.Header>
        <Wrapper className="p-3">
            Header
        </Wrapper>
    </Layout.Header>
    <Layout.Sidebar>
        <Wrapper className="p-3">
            Sidebar
        </Wrapper>
    </Layout.Sidebar>
    <Layout.Content>
        <Wrapper className="p-3">
            Content
        </Wrapper>
    </Layout.Content>
    <Layout.Footer>
        <Wrapper className="p-3">
           Footer
        </Wrapper>
    </Layout.Footer>
</Layout>
`;
const layoutSidebarFullCode = `<Layout type={LayoutType.FullSidebar}>
    <Layout.Header>
        <Wrapper className="p-3">
            Header
        </Wrapper>
    </Layout.Header>
    <Layout.Sidebar>
        <Wrapper className="p-3">
            Sidebar
        </Wrapper>
    </Layout.Sidebar>
    <Layout.Content>
        <Wrapper className="p-3">
            Content
        </Wrapper>
    </Layout.Content>
    <Layout.Footer>
        <Wrapper className="p-3">
           Footer
        </Wrapper>
    </Layout.Footer>
</Layout>
`;













